// @storybook-skip: app-local report slot bound to the app store and report feedback dialog params
import { useAppStore } from "@hooks/useAppStore";
import FullReportDialog from "@memorang/features/src/session-analysis/FullReportDialog";
import { events as sessionAnalysisEvents } from "@memorang/features/src/session-analysis/constants";
import { formatDate } from "@memorang/helpers/date";
import type { ReportDetails } from "@memorang/types";
import Box from "@memorang/ui/components/Box";
import { TipCallout } from "@memorang/ui/components/TipCallout";
import { useAppTheme } from "@memorang/ui/useAppTheme";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useReportFeedbackDialogParams } from "../hooks/useReportFeedbackDialogParams";

type Props = {
	reportDetails: ReportDetails;
	tenantName: string;
	outputLanguage: string;
	preview: {
		text: string;
		isLoading: boolean;
		isError: boolean;
	};
	fullReport: {
		data: string;
		isLoading: boolean;
		streaming: boolean;
		canOpen: boolean;
	};
	handleTracking: (
		eventName: string,
		params?: Record<string, string | number>,
	) => void;
};

export const SessionTipSlotView = ({
	reportDetails,
	tenantName,
	outputLanguage,
	preview,
	fullReport,
	handleTracking,
}: Props) => {
	const theme = useAppTheme();
	const { t } = useTranslation();
	const [showFullReport, setShowFullReport] = useState(false);

	const appFont = useAppStore((s) => s.app.font);
	const fontSize = useAppStore((s) => s.app.fontSizes?.stem) || 16;

	const feedbackDialogParams = useReportFeedbackDialogParams({
		reportDetails,
		tenantName,
		outputLanguage,
	});

	const handleSessionAnalysisTracking = useCallback(
		(eventName: string, params?: Record<string, string | number>) => {
			handleTracking(eventName, {
				feature: "AI_SESSION_ANALYSIS",
				...(params ?? {}),
			});
		},
		[handleTracking],
	);

	const handleOpenFullReport = () => {
		handleSessionAnalysisTracking(sessionAnalysisEvents.viewReportClicked);
		setShowFullReport(true);
	};

	return (
		<>
			<Box padding={16}>
				<TipCallout
					text={preview.text}
					isLoading={preview.isLoading}
					isError={preview.isError}
					onActionPress={fullReport.canOpen ? handleOpenFullReport : undefined}
					actionLabel={t("report.readFullInsights")}
				/>
			</Box>
			{showFullReport ? (
				<FullReportDialog
					sessionAnalysisData={fullReport.data}
					visible={showFullReport}
					handleDismiss={() => setShowFullReport(false)}
					feedbackDialogParams={feedbackDialogParams}
					handleTracking={handleSessionAnalysisTracking}
					sessionMetadata={{
						sessionTitle: reportDetails.examName,
						sessionDate: formatDate(reportDetails.sessionDate),
						numItems: reportDetails.numQuestions,
					}}
					streaming={fullReport.streaming}
					loading={fullReport.isLoading}
					appFont={appFont}
					fontSize={fontSize}
					style={{
						backgroundColor: theme.colors.surface,
					}}
				/>
			) : null}
		</>
	);
};
